import React, { useCallback, useEffect, useState } from 'react';

import 'mapbox-gl/dist/mapbox-gl.css';
import Map, { Marker } from 'react-map-gl';

import { MapViewState, ViewStateChangeParameters } from '@deck.gl/core';
import { DeckGL } from '@deck.gl/react';
import { Box } from '@mui/joy';

import arrowIcon from '../../icons/arrow.svg';
import homeIcon from '../../icons/home.svg';
import { Position } from '../../types/Geometry';
import { GeoFence } from '../../types/Plan';
import DronePathLayer from './layers/DronePathLayer';
import GeoFenceLayer from './layers/GeoFenceLayer';
import OverlayMenu, { DisplayedLayers } from './OverlayMenu';

export interface DroneMapPanelProps {
  dronePosition: Position;
  droneHeading: number; // degrees, clockwise from north
  initialMapCenterPosition: Position;
  dronePath: Position[];
  geoFences: GeoFence[];
  pilotPosition?: Position;
  droneStatus?: 'connected' | 'lost';
}

const DroneMapPanel: React.FC<DroneMapPanelProps> = ({
  dronePosition,
  droneHeading,
  initialMapCenterPosition,
  dronePath,
  geoFences,
  pilotPosition,
  droneStatus = 'lost'
}) => {
  const [viewState, setViewState] = useState<MapViewState>({
    ...initialMapCenterPosition,
    zoom: 16,
    bearing: 0,
    pitch: 0
  });
  const [follow, setFollow] = useState(true);
  const [userMoved, setUserMoved] = useState(false);
  const [displayedLayers, setDisplayedLayers] = useState<DisplayedLayers>({
    geoFences: true,
    dronePath: true,
    noFlyZones: true,
    wayPoints: true
  });

  // Recenter map when drone moves
  useEffect(() => {
    if (follow) {
      setViewState((prev) => ({
        ...prev,
        ...dronePosition
      }));
    }
  }, [dronePosition, follow]);

  const handleDragStart = useCallback(() => {
    setFollow(false);
    setUserMoved(true);
  }, []);

  const recenter = useCallback(() => {
    setFollow(true);
    setUserMoved(false);
  }, [dronePosition]);

  const handleViewStateChange = ({ viewState: newView, interactionState }: ViewStateChangeParameters<MapViewState>) => {
    setViewState(newView);
    if (interactionState.isDragging) {
      setFollow(false);
      setUserMoved(true);
    }
  };

  const MAPBOX_TOKEN = 'pk.eyJ1IjoianVsaWFub2VzIiwiYSI6ImNrOGNwbXVzbTA2bnMzZW95bmhvemIwbGkifQ.vHzrkhopow_IrtZm5yP5iA';

  const markerScale = Math.max(viewState.zoom / 15, 0.6);
  const ringColor = droneStatus === 'connected' ? '#22c55e' : '#ef4444';

  return (
    <Box className='relative w-full h-full'>
      <DeckGL
        controller
        style={{ position: 'absolute', width: '100%', height: '100%' }}
        initialViewState={{
          ...initialMapCenterPosition,
          zoom: 16
        }}
        viewState={viewState}
        onViewStateChange={handleViewStateChange as any}
        layers={[
          displayedLayers.geoFences && new GeoFenceLayer({ geoFences }),
          displayedLayers.dronePath && new DronePathLayer({ dronePath })
        ]}
      >
        <Map
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle='mapbox://styles/mapbox/satellite-v9'
          {...viewState}
          onDragStart={handleDragStart}
          style={{ width: '100%', height: '100%' }}
        >
          <Marker longitude={viewState.longitude} latitude={viewState.latitude} anchor='center'>
            <img src={homeIcon} alt='Home' className='w-6 h-6' />
          </Marker>
          <Marker longitude={dronePosition.longitude} latitude={dronePosition.latitude} anchor='center'>
            <div
              style={{
                transform: `rotate(${droneHeading - (viewState.bearing ?? 0)}deg) scale(${markerScale})`,
                transformOrigin: 'center',
                borderColor: ringColor
              }}
              className='border-2 rounded-full p-1 bg-neutral-900'
            >
              <img src={arrowIcon} alt='Drone' className='w-4 h-4' />
            </div>
          </Marker>
          {pilotPosition && (
            <Marker longitude={pilotPosition.longitude} latitude={pilotPosition.latitude} anchor='center'>
              <div className='w-3 h-3 bg-blue-500 rounded-full border border-white' />
            </Marker>
          )}
        </Map>
      </DeckGL>

      <Box id='toolbar' className='absolute top-2 left-2 z-[1]'>
        <OverlayMenu
          userMoved={userMoved}
          recenter={recenter}
          displayedLayers={displayedLayers}
          updateDisplayedLayers={(updatedLayers) => setDisplayedLayers((prev) => ({ ...prev, ...updatedLayers }))}
        />
      </Box>
    </Box>
  );
};

export default DroneMapPanel;
